import {
  type ParamListBase,
  ThemeProvider as NavigationThemeProvider,
  useTheme as useNavigationTheme,
} from '@react-navigation/native';
import { createNativeStackNavigator, type NativeStackNavigationOptions } from '@react-navigation/native-stack';
import { createStackNavigator, type StackNavigationOptions } from '@react-navigation/stack';
import { createTheme as createDSTheme } from 'app/shopify';
import type { SpecificRouteProp } from 'app/types/navigation';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { useUISize } from 'app/utils/web-helpers';
import { createElement, type ComponentProps, type ComponentType, type ReactNode } from 'react';
import { Platform } from 'react-native';
import { DesktopHeaderRenderer } from './desktop/header';
import { HeaderLeftEmpty, HeaderRightEmpty } from './header-placeholders';
import { renderHeaderLeftBack, renderHeaderLeftInvite, renderHeaderRightNotifications } from './header-renderers';
import { createModalNavigatorOptions } from './modal-navigator-options';
import { createScreenOptions } from './screen-options';

const theme = createDSTheme();
const NavigationThemeProviderWithOptionalChildren = NavigationThemeProvider as ComponentType<
  Omit<ComponentProps<typeof NavigationThemeProvider>, 'children'> & { children?: ReactNode }
>;

interface NavigatorOptions {
  isModal?: boolean;
  withChat?: boolean;
  withInvite?: boolean;
}

export function createDefaultNativeStackNavigator<T extends ParamListBase>(options: NavigatorOptions = {}) {
  const StackWeb = createStackNavigator<T>();
  const StackNative = createNativeStackNavigator<T>();

  function NavigatorWeb({ screenOptions, ...rest }: ComponentProps<(typeof StackWeb)['Navigator']>) {
    const { isLargeUI } = useUISize();
    const navigationTheme = useNavigationTheme();

    const navigator = createElement(StackWeb.Navigator, {
      ...rest,
      screenOptions: (props) => {
        const navigation = props.navigation;
        const route = props.route as SpecificRouteProp;

        const state = navigation.getState();
        const isFirstInStack = state.index === 0;
        const isOutsideScreen = navigation.canGoBack() && isFirstInStack && route.name === 'one-listing';

        const opts = (
          options.isModal ? createModalNavigatorOptions(navigation, route) : createScreenOptions(route)
        ) as StackNavigationOptions;

        const showChatButtonOnRight = options.withChat !== false;
        const showInviteButtonOnLeft =
          options.withInvite !== false && isFeatureEnabled(FeatureFlag.invite) && isFirstInStack;

        const baseOptions: StackNavigationOptions = {
          // Modal scenes stay transparent while their screen cards remain opaque.
          cardStyle: options.isModal ? { backgroundColor: theme.colors.page } : undefined,
          headerBackButtonDisplayMode: 'minimal',
          headerTintColor: theme.colors['white-label.header.on-header'],
          headerStyle: {
            backgroundColor: theme.colors['white-label.header.bg'],
          },
          ...opts,
        };

        const screenOpts = typeof screenOptions === 'function' ? screenOptions(props) : screenOptions;

        if (isLargeUI) {
          return {
            ...baseOptions,
            header: options.isModal ? undefined : DesktopHeaderRenderer,
            ...screenOpts,
          };
        }

        return {
          ...baseOptions,
          headerRight: showChatButtonOnRight ? renderHeaderRightNotifications : HeaderRightEmpty,
          headerLeft: isOutsideScreen
            ? renderHeaderLeftBack
            : showInviteButtonOnLeft
              ? renderHeaderLeftInvite
              : options.isModal
                ? baseOptions.headerLeft
                : navigation.canGoBack()
                  ? renderHeaderLeftBack
                  : HeaderLeftEmpty,
          ...screenOpts,
        };
      },
    });

    if (!options.isModal) {
      return navigator;
    }

    return createElement(
      NavigationThemeProviderWithOptionalChildren,
      {
        value: {
          ...navigationTheme,
          colors: { ...navigationTheme.colors, background: 'transparent' },
        },
      },
      navigator,
    );
  }

  function NavigatorNative({ screenOptions, ...rest }: ComponentProps<(typeof StackNative)['Navigator']>) {
    return createElement(StackNative.Navigator, {
      ...rest,
      screenOptions: (props) => {
        const navigation = props.navigation;
        const route = props.route as SpecificRouteProp;

        const state = navigation.getState();
        const isFirstInStack = state.index === 0;
        const isOutsideScreen = navigation.canGoBack() && isFirstInStack && route.name === 'one-listing';

        const opts = (
          options.isModal ? createModalNavigatorOptions(navigation, route) : createScreenOptions(route)
        ) as NativeStackNavigationOptions;

        const showChatButtonOnRight = options.withChat !== false;
        const showInviteButtonOnLeft =
          options.withInvite !== false && isFeatureEnabled(FeatureFlag.invite) && isFirstInStack;

        const baseOptions: NativeStackNavigationOptions = {
          headerBackButtonDisplayMode: 'minimal',
          headerBackButtonMenuEnabled: false,
          headerTintColor: theme.colors['white-label.header.on-header'],
          headerStyle: {
            backgroundColor: theme.colors['white-label.header.bg'],
          },
          contentStyle: {
            backgroundColor: theme.colors.page,
          },
          ...opts,
        };

        const screenOpts = typeof screenOptions === 'function' ? screenOptions(props) : screenOptions;

        return {
          ...baseOptions,
          headerRight: showChatButtonOnRight ? renderHeaderRightNotifications : HeaderRightEmpty,
          headerLeft: isOutsideScreen
            ? renderHeaderLeftBack
            : showInviteButtonOnLeft
              ? renderHeaderLeftInvite
              : options.isModal
                ? baseOptions.headerLeft
                : HeaderLeftEmpty,
          ...screenOpts,
        };
      },
    });
  }

  function NavigatorResponsive(props: ComponentProps<(typeof StackNative)['Navigator']>) {
    const { isSmallUI } = useUISize();

    if (Platform.OS !== 'web' && isSmallUI) {
      return createElement(NavigatorNative, props);
    }

    // Types of NavigatorWeb vs NavigatorNative props are not perfectly compatible,
    // but at runtime we only ever use one branch based on isMobileUI.
    return createElement(NavigatorWeb, props as unknown as ComponentProps<(typeof StackWeb)['Navigator']>);
  }

  return {
    Group: StackNative.Group,

    Navigator: NavigatorResponsive as any,
    Screen: StackNative.Screen,
  };
}
